import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import useApi from "../../hooks/useApi";
import { useUser } from "../../hooks/useUser";
const tipos = ["Solista", "Grupo Musical"];

const sugerirUsuario = (nombre = "") => {
  const limpio = String(nombre)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 30);
  if (limpio.length >= 3) return limpio;
  return "artista_music";
};

export default function DirectLinkActivar() {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const token = query.get("intent");
  const navigate = useNavigate();

  // Clave de un solo uso que trae el enlace del correo de verificación; sin
  // ella el backend no deja crear la cuenta. Se guarda por solicitud en
  // sessionStorage para que sobreviva a una recarga, y se saca de la barra de
  // direcciones para que no quede en el historial ni se comparta al copiar
  // el link.
  const claveKey = `direct-link-clave:${token}`;
  const [clave] = useState(() => {
    const deUrl = query.get("clave");
    try {
      if (deUrl) sessionStorage.setItem(claveKey, deUrl);
      return deUrl || sessionStorage.getItem(claveKey) || "";
    } catch (e) {
      return deUrl || "";
    }
  });

  useEffect(() => {
    if (!query.get("clave")) return;
    query.delete("clave");
    navigate(`${location.pathname}?${query.toString()}`, { replace: true });
  }, []);
  const { loadApi, loadingApi, loggedApi, tokenDataApi } = useApi();
  const { user, updateUser } = useUser();
  const [intent, setIntent] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState({
    tipo: "Grupo Musical",
    idioma: "Español",
    distribucion: "Independiente",
  });

  const currentEmail = useMemo(
    () => String(user?.username || "").toLowerCase(),
    [user?.username]
  );

  const cargar = () => {
    if (!token) {
      setError("Solicitud no encontrada.");
      return;
    }
    loadApi(
      `general/direct-links/intent/${encodeURIComponent(token)}`,
      false,
      "get"
    )
      .then(({ data }) => {
        setIntent(data);
        setForm((prev) => ({
          ...prev,
          name: prev.name || sugerirUsuario(data.projectName),
        }));
      })
      .catch((e) => setError(e.message));
  };

  useEffect(() => {
    cargar();
  }, [token]);

  const change = (name) => (e) =>
    setForm((prev) => ({ ...prev, [name]: e.target.value }));

  const activar = async () => {
    setError("");
    try {
      const { data } = await loadApi(
        `general/direct-links/intent/${encodeURIComponent(token)}/activate`,
        false,
        "post",
        { ...form, clave }
      );
      try {
        sessionStorage.removeItem(claveKey);
      } catch (e) {}
      setNotice(
        data.message ||
          "Tu cuenta quedó creada y el pedido fue enviado al difusor."
      );
      navigate(`/login?intent=${encodeURIComponent(token)}`);
    } catch (e) {
      setError(e.message);
      cargar();
    }
  };

  const reclamar = async () => {
    setError("");
    try {
      const { data } = await loadApi(
        `artista/direct-links/claim/${encodeURIComponent(token)}`,
        true,
        "post",
        {}
      );
      await updateUser();
      navigate(
        `/artista/envios?pedido=${encodeURIComponent(
          data.envioId || ""
        )}&direct=1`
      );
    } catch (e) {
      setError(e.message);
    }
  };

  const reenviarVerificacion = async () => {
    setError("");
    setNotice("");
    try {
      const { data } = await loadApi(
        "resend_verification_code",
        false,
        "post",
        {
          username: intent.email,
          directIntent: token,
        }
      );
      setNotice(
        data.message ||
          "Si la cuenta está pendiente de verificar, reenviamos el correo."
      );
    } catch (e) {
      setError(e.message);
    }
  };

  if (!intent && !error) {
    return (
      <Stack minHeight="100vh" alignItems="center" justifyContent="center">
        <Typography>Cargando tu pedido...</Typography>
      </Stack>
    );
  }

  if (error && !intent) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="warning">{error}</Alert>
      </Container>
    );
  }

  const cuentaCorrecta =
    loggedApi &&
    tokenDataApi?.role === "artista" &&
    currentEmail === String(intent?.email || "").toLowerCase();

  // El equipo devolvió el pago desde el panel de admin: la solicitud quedó
  // cerrada y ya no se puede activar ni vincular.
  if (intent?.status === "REFUNDED") {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="info">
          Esta solicitud fue reembolsada y quedó cerrada. Si quieres volver a
          enviarla, puedes hacer una nueva desde el Direct Link de{" "}
          {intent.difusor?.nombre_media || "el difusor"}.
        </Alert>
      </Container>
    );
  }

  if (intent?.status === "COMPLETED") {
    const pendienteVerificar =
      intent.accountRole === "artista" && intent.accountReady === false;

    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card sx={{ borderRadius: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Alert severity="success">
              Tu pedido ya está registrado en RadarFreim.
            </Alert>
            <Typography mt={2} color="text.secondary">
              {pendienteVerificar
                ? "Solo falta verificar tu correo para entrar y seguir el pedido desde Mis envíos."
                : "Entra a tu cuenta para ver el estado del pedido en Mis envíos."}
            </Typography>

            {notice && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {notice}
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {loggedApi && !cuentaCorrecta && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Estás conectado con otra cuenta. Para abrir este pedido, inicia
                con {intent.email}.
              </Alert>
            )}

            <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} mt={3}>
              {pendienteVerificar && (
                <LoadingButton
                  loading={loadingApi.includes(
                    "post__resend_verification_code"
                  )}
                  variant="outlined"
                  onClick={reenviarVerificacion}
                >
                  Reenviar verificación
                </LoadingButton>
              )}
              <Button
                onClick={() =>
                  navigate(
                    cuentaCorrecta
                      ? `/artista/envios?pedido=${encodeURIComponent(
                          intent.envioId || ""
                        )}&direct=1`
                      : `/login?intent=${encodeURIComponent(token)}`
                  )
                }
              >
                {cuentaCorrecta
                  ? "Ver mi pedido"
                  : loggedApi
                  ? "Cambiar de cuenta"
                  : "Iniciar sesión"}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (!["PAID", "ORDER_CREATED"].includes(intent?.status)) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="info">
          El pago de este pedido todavía no está confirmado.
        </Alert>
      </Container>
    );
  }

  return (
    <Box minHeight="100vh" bgcolor="#f4f4f6" py={{ xs: 3, md: 6 }}>
      <Container maxWidth="sm">
        <Card sx={{ borderRadius: 4 }}>
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Typography variant="h4" fontWeight={900}>
              ¡Pago confirmado!
            </Typography>
            <Typography color="text.secondary" mt={1}>
              {intent.status === "ORDER_CREATED"
                ? `${intent.difusor?.nombre_media} ya recibió tu solicitud.`
                : `Tu pago está confirmado para ${intent.difusor?.nombre_media}.`}{" "}
              {!intent.accountExists && !intent.emailVerified
                ? "Antes de enviar el pedido necesitamos verificar que este correo realmente te pertenece."
                : "Ahora terminemos de vincular tu cuenta para que puedas darle seguimiento."}
            </Typography>

            <Alert
              severity={
                !intent.accountExists && !intent.emailVerified
                  ? "info"
                  : "success"
              }
              sx={{ mt: 3 }}
            >
              {!intent.accountExists && !intent.emailVerified
                ? "Revisa tu correo y abre el enlace de verificación. El difusor no verá la solicitud hasta que confirmes tu identidad."
                : intent.contentType === "evento"
                ? "Una vez enviado, el difusor tendrá hasta 5 días para atenderlo, sin rebasar la fecha del evento. Si vence el plazo, los 5 RadarCoins regresan a tu cuenta."
                : "Una vez enviado, el difusor tendrá hasta 10 días para atenderlo. Si vence el plazo, los 5 RadarCoins regresan automáticamente a tu cuenta."}
            </Alert>

            {error && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {error}
              </Alert>
            )}

            {intent.accountExists ? (
              <Stack spacing={2} mt={4}>
                <Typography variant="h6" fontWeight={900}>
                  Ya tienes una cuenta en RadarFreim
                </Typography>
                <Typography color="text.secondary">
                  El correo {intent.email} ya está registrado. Inicia sesión
                  con esa cuenta para vincular este pedido sin crear un perfil
                  duplicado.
                </Typography>

                {cuentaCorrecta ? (
                  <LoadingButton
                    loading={loadingApi.includes(
                      `post__artista/direct-links/claim/${token}`
                    )}
                    onClick={reclamar}
                  >
                    Vincular pedido a mi cuenta
                  </LoadingButton>
                ) : (
                  <>
                    {loggedApi && (
                      <Alert severity="warning">
                        Estás conectado con otra cuenta. Cierra sesión e inicia
                        con {intent.email}.
                      </Alert>
                    )}
                    <Button
                      LinkComponent={Link}
                      to={`/login?intent=${encodeURIComponent(token)}`}
                    >
                      Iniciar sesión
                    </Button>
                  </>
                )}
              </Stack>
            ) : !intent.emailVerified || !clave ? (
              <Stack spacing={2} mt={4}>
                <Typography variant="h6" fontWeight={900}>
                  {intent.emailVerified
                    ? "Abre el enlace de tu correo"
                    : "Verifica tu correo"}
                </Typography>
                <Typography color="text.secondary">
                  {intent.emailVerified
                    ? `Para crear tu acceso necesitamos que entres desde el enlace que enviamos a ${intent.email}. Si lo abriste en otro navegador o ya pasaron 24 horas, pide uno nuevo.`
                    : `Enviamos un enlace a ${intent.email}. Cuando lo abras volverás aquí para crear tu acceso y entonces enviaremos la solicitud a ${intent.difusor?.nombre_media}.`}
                </Typography>

                {notice && <Alert severity="info">{notice}</Alert>}

                <LoadingButton
                  loading={loadingApi.includes(
                    "post__resend_verification_code"
                  )}
                  variant="outlined"
                  onClick={reenviarVerificacion}
                >
                  Reenviar correo de verificación
                </LoadingButton>
              </Stack>
            ) : (
              <Stack spacing={2.5} mt={4}>
                <Typography variant="h6" fontWeight={900}>
                  Completa tu registro
                </Typography>
                <Typography color="text.secondary">
                  Correo verificado. Ya tenemos el nombre de tu proyecto,
                  responsable, país y género. Solo falta crear tu acceso a
                  RadarFreim.
                </Typography>

                <TextField
                  label="Nombre de usuario"
                  placeholder="lostomacitos"
                  value={form.name || ""}
                  onChange={change("name")}
                  helperText="3 a 30 caracteres: letras, números o guion bajo."
                  fullWidth
                />
                <TextField
                  select
                  label="Tipo de proyecto"
                  value={form.tipo}
                  onChange={change("tipo")}
                  fullWidth
                >
                  {tipos.map((tipo) => (
                    <MenuItem key={tipo} value={tipo}>
                      {tipo}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Crea una contraseña"
                  type="password"
                  value={form.password || ""}
                  onChange={change("password")}
                  fullWidth
                />
                <TextField
                  label="Repite la contraseña"
                  type="password"
                  value={form.password2 || ""}
                  onChange={change("password2")}
                  fullWidth
                />

                <LoadingButton
                  loading={loadingApi.includes(
                    `post__general/direct-links/intent/${token}/activate`
                  )}
                  onClick={activar}
                >
                  Crear mi cuenta y enviar pedido
                </LoadingButton>

                <Typography variant="caption" color="text.secondary">
                  Tu correo ya fue verificado. Al crear la cuenta, la solicitud
                  quedará enviada al difusor y podrás seguirla desde RadarFreim.
                </Typography>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
